import DOMPurify from 'dompurify'

/**
 * XSS 净化工具
 *
 * 背景：项目中 CommonTable/Column.vue、WidgetItem/index.vue 等全局组件会通过
 * v-html 渲染接口返回的字段值或 formatter 的返回值，存在存储型 XSS 风险。
 * 这里统一在 v-html 出口处对内容做净化，移除脚本、事件处理器等危险内容，
 * 同时保留常规的展示型标签（加粗、换行、高亮等）。
 */

// 全局禁止 javascript: 等危险协议的属性值
// RETURN_TRUSTED_TYPE 关闭，直接返回字符串，便于在模板中使用
DOMPurify.setConfig({
  // 禁止使用 data 属性以外的内联事件
  FORBID_ATTR: [],
  FORBID_TAGS: [],
})

// 额外加固：移除所有以 on 开头的事件属性，拦截 javascript: 协议
DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
  // 清理所有 on* 事件属性（onerror、onclick 等）
  for (const attr of Array.from(node.attributes)) {
    if (/^on/i.test(attr.name)) {
      node.removeAttribute(attr.name)
    }
  }
  // 对 target=_blank 的链接补充 rel，避免 reverse tabnabbing
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

/**
 * 净化用于 v-html 渲染的 HTML 字符串。
 * 保留常见的展示型标签，剔除 script、iframe、事件处理器、危险协议等。
 *
 * @param dirty 待净化内容，可为任意类型（非字符串会安全转换/返回空串）
 * @returns 可安全用于 v-html 的字符串
 */
export const safeHtml = (dirty: unknown): string => {
  if (dirty === null || dirty === undefined) return ''
  // 数字 0 等需要原样展示，转成字符串再净化
  const input = typeof dirty === 'string' ? dirty : String(dirty)
  return DOMPurify.sanitize(input, {
    // 允许的展示型标签白名单
    ALLOWED_TAGS: [
      'a', 'b', 'br', 'div', 'em', 'i', 'img', 'li', 'ol', 'p',
      'span', 'strong', 'sub', 'sup', 'u', 'ul', 'font', 'small', 'mark',
    ],
    ALLOWED_ATTR: [
      'class', 'style', 'title', 'href', 'target', 'rel',
      'src', 'alt', 'width', 'height', 'color',
    ],
    // 仅允许安全协议
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
  })
}

/**
 * 严格净化：仅返回纯文本，剔除所有标签。
 * 适用于“本应是纯文本，但保险起见防注入”的场景。
 */
export const safeText = (dirty: unknown): string => {
  if (dirty === null || dirty === undefined) return ''
  const input = typeof dirty === 'string' ? dirty : String(dirty)
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
}

export default safeHtml

/**
 * 校验并返回安全的可打开 URL，仅允许 http(s) 协议。
 * 不安全时返回空串，调用方据此决定是否 window.open。
 *
 * @param url 待校验的 URL
 * @returns 安全的 URL 或空串
 */
export const safeOpenUrl = (url: unknown): string => {
  if (typeof url !== 'string' || !url) return ''
  const trimmed = url.trim()
  // 允许相对路径（不含协议）
  if (/^(https?:)?\/\//i.test(trimmed) || /^\/[^/]/.test(trimmed)) {
    // 显式拦截伪装协议
    if (/^\s*javascript:/i.test(trimmed) || /^\s*data:/i.test(trimmed) || /^\s*vbscript:/i.test(trimmed)) {
      return ''
    }
    return trimmed
  }
  // 其余一律视为不安全
  return ''
}
